import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography, Chip, useMediaQuery, useTheme } from '@mui/material'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Search } from 'lucide-react'
import { CertificatesDateRangeFilter } from 'components/pages/certificates/CertificatesDateRangeFilter'
import { MobileDateRangeDrawer } from './MobileDateRangeDrawer'
import { ProvenanceOwnerSearchPicker, type ProvenanceOwner } from './ProvenanceOwnerSearchPicker'
import { LocationSearchPicker, type Location } from './LocationSearchPicker'
import { SCAN_STATUS_FILTER_OPTIONS } from '@repo/ui'
import { StatusPillFilter } from 'components/pages/certificates/StatusPillFilter'
import { FieldTrigger } from 'components/UI/FieldTrigger'
import { FieldLabel } from 'components/UI/FieldLabel'
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter'
import { FILTER_PANEL_CONTENT_SX } from 'components/UI/filterPanelSx'
import { createApiCall } from 'services'
import { useAutoApply } from 'hooks/useAutoApply'
import { font } from 'settings/tokens';

export interface ScansFilterValues {
  ownerIds?: string[]
  statuses?: string[]
  dateFrom?: string
  dateTo?: string
  locationQuery?: string
  locationIds?: string[]
  selectedRange?: 'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null
}

interface ScansFilterDrawerProps {
  open: boolean
  onClose: () => void
  onApply: (values: ScansFilterValues) => void
  certificateId: string
  initialValues?: Partial<ScansFilterValues>
}

 // VERA-476: the Status axis filters SCAN OUTCOMES, matching the chip on each list row.
 //
 // It previously offered CERTIFICATE statuses (active / in_transfer / out_transfer /
 // cancel_request / stolen / lost) and matched them against `scan.status`, which the
 // `/scan-log/public/:id` payload never populates — so "Active" matched everything and
 // every other option matched nothing. Options now come from the shared resolver so the
 // filter, the row chip, and the map pin colours can never drift apart.
 const buildStatusOptions = (theme: any) =>
   SCAN_STATUS_FILTER_OPTIONS.map(({ key, label }) => {
     /*
      * FALLBACK, not defensive padding — this map WILL fall behind again.
      *
      * The options come from the shared resolver; these tones are a local copy. So
      * every status the resolver gains is a key missing here, and the crash is
      * silent to TypeScript: `Record<string, X>` indexes to `X`, never
      * `X | undefined`, without `noUncheckedIndexedAccess`. The compiler asserts a
      * value that isn't there and `meta.color` throws at render.
      *
      * That already happened. VRG-252 added `connected` to the resolver on
      * 2026-08-19; this map was last touched 2026-07-31, so opening the Scans
      * sub-view threw a TypeError on a shipped surface, caught 2026-08-23.
      *
      * Neutral is the right fallback: an unknown status is one this UI has no
      * opinion about, and grey says that. A missing tone must never again be the
      * difference between a chip and a white screen.
      */
     const meta = SCAN_STATUS_TONE_STYLES[key] ?? SCAN_STATUS_TONE_NEUTRAL
     return { key, label, styles: { color: meta.color, backgroundColor: meta.backgroundColor } }
   })

 const SCAN_STATUS_TONE_NEUTRAL = { color: '#3F3F46', backgroundColor: '#F4F4F5' }

 /*
  * ⚠️ A DUPLICATE of tone data `packages/ui/utils/scanStatus.ts` already owns (it carries
  * `tone` and a per-status `color`), and it has already diverged: the shared module
  * documents `connected` as "the same brand purple as verified", while `verified` is GREY
  * here. Converging on the shared ramp is the §36 fix and would change how these chips
  * look, so it wants its own verify pass rather than riding along with a crash fix.
  *
  * `connected` is therefore grey to match this map's own `verified`, not purple to match
  * the shared one — deliberately consistent with its neighbours rather than importing half
  * of a different ramp and leaving one odd chip.
  */
 const SCAN_STATUS_TONE_STYLES: Record<string, { color: string; backgroundColor: string }> = {
   verified: { color: '#3F3F46', backgroundColor: '#F4F4F5' },
   connected: { color: '#3F3F46', backgroundColor: '#F4F4F5' },
   invalid: { color: '#92400E', backgroundColor: '#FEF3C7' },
   /*
    * NEUTRAL, not the red it carried until VRG-491 — and note the LABEL beside it has
    * always come from the shared resolver, so this option rendered the word "Opened" in
    * alert red: the corrected word wearing the overclaim it was meant to remove.
    *
    * A cut tamper loop means the seal was opened. The tag can report that the circuit
    * broke; it cannot report who broke it or why, and on a closure tag the ordinary cause
    * is the owner opening what they bought. Only an INVALID scan earns an alert tone.
    *
    * Deliberately the same literal as this map's own `SCAN_STATUS_TONE_NEUTRAL` rather
    * than an import of the resolver's ramp: converging this whole map onto the shared
    * tones is the §36 fix noted above and changes every chip here, so it still wants its
    * own verify pass. This one row was corrected because it is wrong on its own terms.
    */
   tampered: { color: '#3F3F46', backgroundColor: '#F4F4F5' },
   link_suspicious: { color: '#9D174D', backgroundColor: '#FCE7F3' },
   link_stale: { color: '#3F3F46', backgroundColor: '#F4F4F5' },
   link_invalid: { color: '#3F3F46', backgroundColor: '#F4F4F5' },
 }

export const ScansFilterDrawer: React.FC<ScansFilterDrawerProps> = ({ open, onClose, onApply, certificateId, initialValues }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>(initialValues?.ownerIds ?? [])
  const [ownerPickerOpen, setOwnerPickerOpen] = useState<boolean>(false)
  const [owners, setOwners] = useState<ProvenanceOwner[]>([])

   const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialValues?.statuses ?? [])
   const STATUS_OPTIONS = useMemo(() => buildStatusOptions(theme), [theme])

  const [dateFrom, setDateFrom] = useState<string>(initialValues?.dateFrom ?? '')
  const [dateTo, setDateTo] = useState<string>(initialValues?.dateTo ?? '')
  const [dateOpen, setDateOpen] = useState<boolean>(false)
  const [selectedRange, setSelectedRange] = useState<'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null>(initialValues?.selectedRange ?? null)

  const [locationQuery, setLocationQuery] = useState<string>(initialValues?.locationQuery ?? '')
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(initialValues?.locationIds ?? [])
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false)
  const [locations, setLocations] = useState<Array<{id: string, location: string, city?: string, country?: string}>>([])
  const [showAllLocationChips, setShowAllLocationChips] = useState<boolean>(false)

  // The drawer stays mounted between opens, so its local state has to be re-seeded
  // from the applied values each time it opens. Without this, every `useState`
  // initialiser above runs ONCE, for the first open, and the sheet then shows whatever
  // it was last left holding — not what is actually applied to the list. Same fix, and
  // the same reasoning, as ActivityFilterDrawer.
  //
  // EVERY field derived from `initialValues` is re-seeded here. A partial re-seed is
  // worse than none: some rows would refresh and others would not, so the sheet would
  // read as a coherent selection while being half stale.
  useEffect(() => {
    if (!open) return
    setSelectedOwnerIds(initialValues?.ownerIds ?? [])
    setSelectedStatuses(initialValues?.statuses ?? [])
    setDateFrom(initialValues?.dateFrom ?? '')
    setDateTo(initialValues?.dateTo ?? '')
    setSelectedRange(initialValues?.selectedRange ?? null)
    setLocationQuery(initialValues?.locationQuery ?? '')
    setSelectedLocationIds(initialValues?.locationIds ?? [])
    // Not from initialValues, but it is a view over the location chip list: a re-seeded
    // selection starts collapsed, exactly as a fresh mount would.
    setShowAllLocationChips(false)
    // Keyed on the OPEN transition only — deliberately not on `initialValues`.
    // ScansTabContent builds that prop as an inline object literal, so its identity
    // changes on every parent render; depending on it would re-run this effect
    // constantly and wipe the selection the user is in the middle of making.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const calculateDateRange = (range: 'last_month' | 'last_3m' | 'last_6m' | 'last_12m') => {
    const now = new Date()
    const from = new Date()
    
    switch (range) {
      case 'last_month':
        from.setMonth(now.getMonth() - 1)
        break
      case 'last_3m':
        from.setMonth(now.getMonth() - 3)
        break
      case 'last_6m':
        from.setMonth(now.getMonth() - 6)
        break
      case 'last_12m':
        from.setMonth(now.getMonth() - 12)
        break
    }
    
    return {
      from: from.toISOString().slice(0, 10),
      to: now.toISOString().slice(0, 10)
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const { data, status } = await createApiCall({
          method: 'GET',
          url: `certificates/public/provenance/${certificateId}?limit=100`,
        })
        if (status === 200 && data?.data) {
          const map = new Map<string, ProvenanceOwner>()
          data.data.forEach((item: any) => {
            if (item.owner_type === 'customer') {
              const email = item.owner?.email
              if (email && !map.has(email)) {
                map.set(email, { id: email, name: `${item.owner?.first_name || ''} ${item.owner?.last_name || ''}`.trim(), email, avatar: item.owner?.avatar, owner_type: 'customer' })
              }
            } else if (item.owner) {
              const id = `company-${item.owner?.name || item.certificate?.company?.name || 'Unknown'}`
              if (!map.has(id)) {
                /*
                 * LOWERCASE, matching the API — and this was a live bug, not a lint.
                 *
                 * This file already COMPARED lowercase (`item.owner_type === 'customer'`
                 * a few lines up) while WRITING capitalized, and the picker's local type
                 * declared the capitalized form, so nothing ever caught the mismatch.
                 * `ProvenanceOwnerSearchPicker` renders an owner's avatar SHAPE from
                 * `owner.owner_type === 'company'`, so a capitalized value made that
                 * comparison false and every brand logo in the Scans owner picker
                 * rendered circular — the exact thing the square-logo change fixes
                 * everywhere else.
                 */
                map.set(id, { id, name: item.owner?.name || item.certificate?.company?.name, owner_type: 'company' })
              }
            }
          })
          setOwners(Array.from(map.values()))
        } else {
          setOwners([])
        }
      } catch {
        setOwners([])
      }
    }
    if (open && certificateId) load()
  }, [open, certificateId])

  useEffect(() => {
    const loadLocations = async () => {
      if (certificateId) {
        try {
          const { data, status } = await createApiCall({
            url: `/scan-log/public/${certificateId}`,
            method: 'GET',
            params: { limit: 1000 }
          })

          if (status === 200 && data?.data) {
            const locationMap = new Map<string, {id: string, location: string, city?: string, country?: string}>()
            
            data.data.forEach((scan: any) => {
              if (scan.metadata?.location) {
                const locationKey = scan.metadata.location
                if (!locationMap.has(locationKey)) {
                  locationMap.set(locationKey, {
                    id: locationKey,
                    location: scan.metadata.location,
                    city: scan.metadata.city,
                    country: scan.metadata.country
                  })
                }
              }
            })

            setLocations(Array.from(locationMap.values()))
          }
        } catch (error) {
          console.error('Error fetching locations:', error)
        }
      }
    }

    if (open && certificateId) loadLocations()
  }, [open, certificateId])

  /*
   * AUTO-APPLY — no Apply button, at either breakpoint. Every control commits on a
   * 400ms debounce (the platform drawer's `AUTO_APPLY_MS`), which is what keeps
   * ticking three status pills to one query instead of three.
   *
   * Interaction-triggered, not state-watched: this drawer re-seeds all SEVEN of its
   * fields from `initialValues` on the open transition, and `ScansTabContent` builds
   * that prop as an inline object literal, so a state watcher would fire on the
   * re-seed and bounce the applied values back on every open.
   *
   * `open` is passed so a tap made in the last 400ms before dismissal is flushed —
   * this drawer stays mounted between opens, so there is no unmount to flush on.
   */
  const { schedule } = useAutoApply(
    () =>
      onApply({
        ownerIds: selectedOwnerIds,
        statuses: selectedStatuses,
        dateFrom,
        dateTo,
        locationQuery,
        locationIds: selectedLocationIds,
        selectedRange,
      }),
    open,
  )

  const handleToggleStatus = (key: string) => {
    setSelectedStatuses(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
    schedule()
  }

  const footer = (
    <FilterSheetFooter
      /* `panel` on desktop: right-aligned, intrinsic-width, 40px — the same footer
         geometry the PLATFORM's filter drawer renders from this same component. A 50/50
         pair of 44px thumb targets is a phone-sheet shape and read as one on a 400px
         pointer-driven side panel. `sheet` on mobile is unchanged. */
      mode={isMobile ? 'sheet' : 'panel'}
      /* RESET ONLY. Omitting `onApply` selects the shared component's auto-apply mode;
         `count` went with the button it labelled. Reset commits (clearing IS the
         change) and still does not close the sheet. */
      onReset={() => {
        setSelectedOwnerIds([])
        setSelectedStatuses([])
        setDateFrom('')
        setDateTo('')
        setLocationQuery('')
        setSelectedLocationIds([])
        setSelectedRange(null)
        schedule()
      }}
    />
  )

  const content = (
    /* The desktop panel supplies its own 16px gutter — DrawerPopup pads its child
       horizontally for bottom sheets ONLY (`px={anchor === 'bottom' ? 2 : 0}`), so
       without this every control here sat flush against the panel edge while the
       panel's own header was inset. 16 and not 24 because DrawerPopup's header is a
       hard-coded 16, and matching the header is what "consistent" means here — the
       same reasoning CertificatesFilter records for its own gutter. */
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: isMobile ? 0 : 2 }}>
      {/* `minHeight: 0` is what makes `overflowY: auto` actually scroll — a flex item's
          default `min-height: auto` sizes it to its CONTENT, so the body would grow past
          its `flex: 1` share and push the footer out of the panel instead of scrolling
          under it. */}
      <Stack sx={{ gap: 2, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {/* Owner */}
        <Stack sx={{ gap: 1 }}>
          <FieldLabel label="Owner" />
          <ProvenanceOwnerSearchPicker
            mobile={isMobile}
            open={ownerPickerOpen}
            onOpen={() => setOwnerPickerOpen(true)}
            onClose={() => setOwnerPickerOpen(false)}
            certificateId={certificateId}
            selectedOwnerIds={selectedOwnerIds}
            onToggleOwnerId={(id, checked) => {
              setSelectedOwnerIds(prev => checked ? [...prev, id] : prev.filter(v => v !== id))
              schedule()
            }}
            onClear={() => {
              setSelectedOwnerIds([])
              schedule()
            }}
            onApply={() => setOwnerPickerOpen(false)}
          />

          {selectedOwnerIds.length > 0 && (
            <Stack spacing={1}>
              <Typography sx={{ color: 'text.primary', fontSize: 14 }}>Selected items: {selectedOwnerIds.length}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {owners
                  .filter(o => selectedOwnerIds.includes(o.id))
                  .map(owner => (
                    <Box
                      key={owner.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: theme => theme.palette.grey[50],
                        borderRadius: '12px',
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      <Typography sx={{ fontSize: 14 }}>{owner.name || owner.email}</Typography>
                      <Box
                        onClick={() => {
                          setSelectedOwnerIds(prev => prev.filter(id => id !== owner.id))
                          schedule()
                        }}
                        sx={{ cursor: 'pointer', fontSize: 16, fontWeight: 'bold' }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Stack>
          )}
        </Stack>

        {/* Status */}
        <Stack sx={{ gap: 1 }}>
          <FieldLabel label="Status" />
          {/* VERA-315: the "Invalid scans" toggle is GONE. Two reasons, either
              of which is sufficient. It was not a status, so it sat inside a
              Status section as a different kind of control answering a different
              kind of question — and the pills below already carry "Invalid" as
              one of the six outcomes, so it duplicated one of them. It was also
              DEAD: `includeInvalid` was passed through onApply and read by
              nobody, so the toggle inflated the Apply count and filtered
              nothing. */}
          {/* VERA-315: the SAME pill toggle the certificates-list filter uses.
              This was a checkbox inside a bordered square box beside a coloured
              pill — three shapes to express one binary, and a different
              interaction from the certificates filter one screen away, where the
              pill IS the control. A checkbox next to a tappable box also asks
              which of the two you are meant to hit.

              `StatusPillFilter` was already generic over its options, so this is
              a mapping rather than a rewrite: the scan tones supply the selected
              fill and text, and the border falls back to the fill (the scan tone
              map defines no edge colour of its own). */}
          <StatusPillFilter
            options={STATUS_OPTIONS.map(opt => ({
              value: opt.key,
              label: opt.label,
              fillColor: opt.styles.backgroundColor,
              borderColor: opt.styles.backgroundColor,
              textColor: opt.styles.color,
            }))}
            selected={selectedStatuses}
            onToggle={handleToggleStatus}
          />
        </Stack>

        {/* VERA-315: ONE label for one field. This section printed a "Date"
            heading and then the range control printed its own "Scan date" label
            directly beneath it — two labels, 20px apart, for a single input, and
            the bare "Date" read as a stray word because the specific label was
            the one below it. The heading now carries the specific name and the
            control's own label is suppressed (`fieldLabel=''`), which is what
            ActivityFilterDrawer already does.

            That also fixes the gap the two fields disagreed on: with the inner
            label gone, "Scan date" and "Location" are both section headings with
            the same 8px gap to their field, instead of one having a heading gap
            and the other a field-label margin. */}
        <Stack sx={{ gap: 1 }}>
          <FieldLabel label="Scan date" />
          <CertificatesDateRangeFilter
            mobile={isMobile}
            selectedRange={selectedRange}
            selectedDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
            open={dateOpen}
            onOpen={() => setDateOpen(true)}
            onClose={() => setDateOpen(false)}
            /* All three are COMPLETE states, so all three commit at once: this
               control picks a single `dateFrom` (with `dateTo` deliberately empty)
               or a named preset. The half-expressed-range hazard belongs to the
               certificates sheet's `mode='range'` calendar, not here. */
            onClear={() => {
              setDateFrom('');
              setDateTo('');
              setSelectedRange(null);
              schedule();
            }}
            onSelectRange={(range) => {
              setSelectedRange(range);
              setDateFrom('');
              setDateTo('');
              schedule();
            }}
            onSelectDate={(d) => {
              setSelectedRange(null);
              setDateFrom(d ? d.toISOString().slice(0, 10) : '');
              setDateTo('');
              schedule();
            }}
            fieldLabel=""
          />

          {isMobile && (
            <MobileDateRangeDrawer
              open={dateOpen}
              title="Scan date"
              selectedRange={selectedRange}
              selectedDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
              onSelectRange={(range) => {
                setSelectedRange(range);
                setDateFrom('');
                setDateTo('');
                schedule();
              }}
              onSelectDate={(d) => {
                setSelectedRange(null);
                setDateFrom(d ? d.toISOString().slice(0, 10) : '');
                setDateTo('');
                schedule();
              }}
              onClear={() => {
                setDateFrom('');
                setDateTo('');
                setSelectedRange(null);
                schedule();
              }}
              onDone={() => setDateOpen(false)}
              onClose={() => setDateOpen(false)}
            />
          )}
        </Stack>

        {/* Location */}
        <Stack sx={{ gap: 1 }}>
          <FieldLabel
            label="Location"
          />
          {/* VERA-315: the shared trigger. This hand-rolled its own surface and
              came out 12px-radius / 40px tall / grey[300] / 14px placeholder,
              directly beneath a date trigger that is 6px-radius / 44px tall /
              border.subtle / 16px — four differences between two fields in one
              sheet. */}
          <FieldTrigger
            icon={<Search size={16} />}
            placeholder='Select locations'
            value={
              selectedLocationIds.length > 0
                ? `${selectedLocationIds.length} location${selectedLocationIds.length !== 1 ? 's' : ''} selected`
                : null
            }
            onClick={() => setLocationPickerOpen(true)}
          />

          {selectedLocationIds.length > 0 && (
            <Stack spacing={1}>
              <Typography sx={{ color: 'text.primary', fontSize: 14 }}>Selected items: {selectedLocationIds.length}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {locations
                  .filter(location => selectedLocationIds.includes(location.id))
                  .slice(0, showAllLocationChips ? selectedLocationIds.length : 6)
                  .map((location) => (
                    <Box
                      key={location.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: theme => theme.palette.grey[50],
                        borderRadius: '12px',
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      <Typography sx={{ fontSize: 14 }}>{location.location}</Typography>
                      <Box
                        onClick={() => {
                          setSelectedLocationIds(prev => prev.filter(id => id !== location.id))
                          schedule()
                        }}
                        sx={{ cursor: 'pointer', fontSize: 16, fontWeight: 'bold' }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
              </Box>
              {selectedLocationIds.length > 6 && !showAllLocationChips && (
                <Box
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'primary.main',
                    fontSize: 14,
                    alignSelf: 'center',
                    px: 1,
                  }}
                  onClick={() => setShowAllLocationChips(true)}
                >
                  Show more
                </Box>
              )}
              {selectedLocationIds.length > 6 && showAllLocationChips && (
                <Box
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'primary.main',
                    fontSize: 14,
                    alignSelf: 'center',
                    px: 1,
                  }}
                  onClick={() => setShowAllLocationChips(false)}
                >
                  Show less
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>

      {footer}
    </Stack>
  )

  return (
    <>
      <DrawerPopup
        title="Filters"
        /* NO 'Cancel' — the header X, which is what DrawerPopup draws when no right
           action is given (and what this drawer's desktop branch already showed).
           Under auto-apply nothing is pending, so "Cancel" would promise a revert
           that cannot happen; Reset clears to empty, which is not the same thing. */
        anchor={isMobile ? 'bottom' : 'right'}
        child={content}
        handleOpen={open}
        handleClose={onClose}
        /* Desktop only — makes the content box FILL the panel so the footer's
           `marginTop: auto` has space to push Reset/Apply to the bottom. See
           `FILTER_PANEL_CONTENT_SX` for why `height: 100%` alone did not. */
        contentSx={isMobile ? undefined : FILTER_PANEL_CONTENT_SX}
        // Mobile bottom sheet is content-height, not full-page (VERA-294 rule; ported
        // from VERA-315's CertificatesFilter fix). Desktop stays a side panel.
        paperSx={isMobile ? { height: 'auto', maxHeight: '85vh' } : { width: 400, maxWidth: '80vw', mr: 2, my: 2, borderRadius: '12px', height: 'calc(100% - 32px)' }}
      />

      <LocationSearchPicker
        mobile={isMobile}
        open={locationPickerOpen}
        onOpen={() => setLocationPickerOpen(true)}
        onClose={() => setLocationPickerOpen(false)}
        certificateId={certificateId}
        selectedLocationIds={selectedLocationIds}
        onToggleLocationId={(id, checked) => {
          setSelectedLocationIds(prev => checked ? [...prev, id] : prev.filter(v => v !== id))
          schedule()
        }}
        onClear={() => {
          setSelectedLocationIds([])
          setLocationPickerOpen(false)
          schedule()
        }}
        onApply={() => setLocationPickerOpen(false)}
      />
    </>
  )
}

export default ScansFilterDrawer


